import { ReparoApi } from './index';

/**
 * Warehouse master-data calls, the missing sibling of `products.ts` and `materials.ts`.
 *
 * Separate from `warehouse-ops.ts`, which is the STN/GRN execution domain and shares nothing
 * with this but a word in its name.
 */

export type BulkUpsertWarehousesError = {
    row: number;
    warehouseId: string;
    reason: string;
};

export type BulkUpsertWarehousesResult = {
    totalRows: number;
    created: number;
    updated: number;
    skipped: number;
    errors: BulkUpsertWarehousesError[];
    dryRun: boolean;
};

/**
 * Columns the upsert importer understands — the download's own header line.
 *
 * `type` is free text, not a fixed vocabulary: 171 of the live warehouses carry a value the
 * WarehouseType enum does not list, so the importer accepts any non-empty string rather than
 * rejecting a warehouse's own exported row.
 */
export const WAREHOUSE_UPSERT_CSV_COLUMNS = [
    'warehouse_id',
    'name',
    'street',
    'city',
    'state',
    'country',
    'zip_code',
    'type',
    'min_capacity',
    'max_capacity',
    'is_active',
    'brand',
] as const;

/**
 * Only the key. A row whose id is NEW must also carry `name` and `type`, which the importer
 * reports per row rather than refusing the whole file for — the address columns default to
 * empty, which is what 760 of the 934 live warehouses already hold.
 */
export const WAREHOUSE_UPSERT_REQUIRED_COLUMNS = ['warehouse_id'] as const;

/**
 * ADMIN only. Updates rows whose `warehouse_id` exists and creates the ones that do not.
 * With `dryRun` the backend runs every check and writes nothing.
 */
export const bulkUpsertWarehouses = async (
    file: File,
    dryRun: boolean,
): Promise<BulkUpsertWarehousesResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/warehouses/bulk-upsert?dryRun=${dryRun}`,
        body: formData,
    });
};
